import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { HomeFeedResponse } from './useHomeFeed';
import { OrderDrawerResponse } from './useGetOrderDrawer';

interface ToggleOrderFlagInput {
    orderId?: string;
    orderIds?: string[];
    manuallyFlagged?: boolean;
}

const toggleOrderFlag = async (data: ToggleOrderFlagInput, sessionId: string | null) => {
    return axios.patch(`${API_URL}/order/flag`, data, {
        headers: {
            Authorization: sessionId
        }
    });
};

export const useToggleOrderFlag = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ToggleOrderFlagInput) => toggleOrderFlag(data, sessionContext?.sessionId ?? null),
        onMutate: async (variables) => {
            if (variables.orderIds?.length || !variables.orderId) return;

            await queryClient.cancelQueries({ queryKey: ['/orders/home-feed-paginated'] });
            await queryClient.cancelQueries({ queryKey: ['/order/drawer', variables.orderId] });

            queryClient.setQueriesData<HomeFeedResponse>({ queryKey: ['/orders/home-feed-paginated'] }, (old) => {
                if (!old) return old;
                return {
                    ...old,
                    orders: old.orders.map(order => {
                        if (order._id === variables.orderId) {
                            return {
                                ...order,
                                manuallyFlagged: !order.manuallyFlagged
                            };
                        }
                        return order;
                    })
                };
            });

            queryClient.setQueryData<OrderDrawerResponse>(['/order/drawer', variables.orderId], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    manuallyFlagged: !old.manuallyFlagged
                };
            });
        },
        onSuccess: (_data, variables) => {
            if (!variables.orderIds?.length) {
                queryClient.invalidateQueries({ queryKey: ['/orders/home-feed-paginated'] });
            }
        },
    });
};
